import React, { useState, useEffect, useContext, useRef } from 'react';
import { UniverseBuilder } from '@baemingo/order-status-events';
import {
  ORDER_ORIGIN,
  ORDER_STATUS,
  SERVICE_TYPE,
} from '@baemingo/order-status-events/dist/types';
import { useSelector, useDispatch, useLogger, useDeviceInfo } from '~Hooks';
import { v4 as uuid } from 'uuid';

import * as actions from '~actions';
import TimeManager from '~Components/TimeManager';
import OrderStatusEventsWorker from '../../Workers/OrderStatusEvents?worker';

// Load our Service Worker
// This will run event computations in a separate thread
const worker = new OrderStatusEventsWorker();

/**
 * Create a context with an empty universe. This context acts as a
 * state that we can access from child components. We use this in app.tsx
 */
const Context = React.createContext(new UniverseBuilder(null).serialize());

export const OrderStatusUniverseProvider: React.FunctionComponent = ({ children }) => {
  // A local universe
  const universe = useRef(new UniverseBuilder(null));

  // Keeps track of the latest worker run ID.
  // Sometimes, multiple runs might run at the same time. We only care about the results from
  // the run with this ID since previous results are outdated.
  const workerRunId = useRef(0);

  // Get events from state. Events are added via UI actions or synced from backend
  const events = useSelector((state) => state.orderStatusEvents);

  // Get snapshot from state. If this changes we override our current universe
  const snapshot = useSelector((state) => state.orderStatusSnapshot.snapshot);

  // We save the serialized universe in state to trigger re-renders
  const [state, setState] = useState(universe.current.serialize());

  const [index, setIndex] = useState(0);

  const mountLogger = useLogger('mount');

  const deviceInfo = useDeviceInfo();
  const isS1F2OrS1F4 = deviceInfo?.isAdyenTerminal || false;

  useEffect(() => {
    mountLogger.info('Mounted: OrderStatusUniverseProvider');
    return () => {
      mountLogger.info('Unmounted: OrderStatusUniverseProvider');
    };
  }, []);

  /**
   * This effect sets up an event listener that takes incoming
   * data from our "add events" worker and updates the state
   */
  useEffect(() => {
    const onWorkerMessage = (e: MessageEvent) => {
      const { id, data } = e.data;

      // If this is stale data, ignore it
      if (id !== workerRunId.current) {
        return;
      }

      universe.current = new UniverseBuilder(data);
      setState(data);
    };

    worker.addEventListener('message', onWorkerMessage);
    return () => worker.removeEventListener('message', onWorkerMessage);
  }, []);

  useEffect(() => {
    workerRunId.current++;

    // Handle event addition in a separate CPU thread
    // using service workers
    if (!isS1F2OrS1F4) {
      worker.postMessage({
        events,
        snapshot,
        id: workerRunId.current,
      });
    }

    const newEvents = events.slice(index);

    newEvents.forEach((event) => {
      try {
        universe.current.addEvent(event);
      } catch (e) {
        console.error(e);
      }
    });

    setState(universe.current.serialize());
    setIndex(events.length);
  }, [events]);

  useEffect(() => {
    if (!snapshot) {
      return;
    }

    universe.current = new UniverseBuilder(snapshot);
    setState(universe.current.serialize());
  }, [snapshot]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useOrderStatusUniverse = () => {
  return useContext(Context);
};

export const useOrderStatusApi = (venueId: string) => {
  const dispatch = useDispatch();
  const logger = useLogger('order-status-universe');

  const generateEventMeta = () => ({
    createdAt: TimeManager.now().toISOString(),
    eventId: uuid(),
    sentAt: null,
    venueId,
  });

  const API = {
    addItem: (
      itemId: string,
      categoryId: string,
      orderId: string,
      comment: string,
      variations: string[],
      removedVariations: string[],
      status: ORDER_STATUS,
      id?: string,
      parentId?: string,
      courseId?: string,
    ) => {
      logger.info('[DEBUG MODE] OrderUniverseAPI: Dispatching item', {
        itemId,
        categoryId,
        orderId,
        comment,
        variations,
        removedVariations,
        id,
        parentId,
        courseId,
      });
      dispatch(
        actions.addOrderStatusEvent({
          type: 'ADD_ITEM',
          id: id || uuid(),
          itemId,
          categoryId,
          orderId,
          comment,
          variations,
          removedVariations,
          initialStatus: status,
          parentId,
          courseId,
          ...generateEventMeta(),
        }),
      );
    },

    addOrder: (
      realId: string,
      comment: string,
      status: ORDER_STATUS,
      serviceType: string | null,
      tableName?: string,
      tableId?: string | null,
      origin?: ORDER_ORIGIN | null,
      desiredTime?: string | null,
      courseId?: string | null,
      fired?: boolean,
    ) => {
      const id = uuid();
      const eventMeta = generateEventMeta();
      logger.info('[DEBUG MODE] OrderUniverseAPI: Dispatching order', {
        data: {
          eventMeta: eventMeta,
          eventId: id,
          realId,
          comment,
          status,
          serviceType,
          tableName,
          tableId,
          origin,
          desiredTime,
          courseId,
          fired,
        },
      });

      dispatch(
        actions.addOrderStatusEvent({
          type: 'ADD_ORDER',
          id,
          comment,
          realId,
          initialStatus: status,
          tableName,
          tableId,
          serviceType: serviceType as SERVICE_TYPE,
          origin: origin,
          estimatedFinishTime: desiredTime,
          desiredTime: desiredTime,
          courseId,
          fired,
          ...eventMeta,
        }),
      );

      return id;
    },

    progressItem: (itemId: string, count = 1) => {
      dispatch(
        actions.addOrderStatusEvent({
          type: 'PROGRESS_ITEM',
          id: itemId,
          progressCount: count,
          ...generateEventMeta(),
        }),
      );
    },

    progressOrder: (orderId: string, count = 1) => {
      dispatch(
        actions.addOrderStatusEvent({
          type: 'PROGRESS_ORDER',
          orderId,
          progressCount: count,
          ...generateEventMeta(),
        }),
      );
    },

    moveOrderToTable: (orderId: string, options: { tableId: string; tableName: string }) => {
      dispatch(
        actions.addOrderStatusEvent({
          type: 'MOVE_ORDER_TO_TABLE',
          orderId,
          tableId: options.tableId,
          tableName: options.tableName,
          ...generateEventMeta(),
        }),
      );
    },

    fireOrder: (orderId: string) => {
      dispatch(
        actions.addOrderStatusEvent({
          type: 'FIRE_ORDER',
          orderId,
          ...generateEventMeta(),
        }),
      );
    },

    fireCourse: (
      courseId: string,
      table: { tableId?: string | null; tableName?: string | null },
    ) => {
      // Fire-by-criteria: matching orders are resolved in the reducer on every
      // device, so this works even when this device's local universe is missing
      // the order (sync lag, stale universe after wake, trimmed orders).
      dispatch(
        actions.addOrderStatusEvent({
          type: 'FIRE_ORDER',
          courseId,
          tableId: table.tableId,
          tableName: table.tableName,
          ...generateEventMeta(),
        }),
      );
    },

    undo: () => {
      dispatch(
        actions.addOrderStatusEvent({
          type: 'UNDO',
          ...generateEventMeta(),
        }),
      );
    },

    cancelItem: (itemId: string, reason?: string) => {
      dispatch(
        actions.addOrderStatusEvent({
          type: 'CANCEL_ITEM',
          id: itemId,
          reason,
          ...generateEventMeta(),
        }),
      );
    },

    confirmCancelItem: (itemId: string) => {
      dispatch(
        actions.addOrderStatusEvent({
          type: 'CONFIRM_CANCEL_ITEM',
          id: itemId,
          ...generateEventMeta(),
        }),
      );
    },
  };

  return API;
};
